import React, { useState } from 'react'
import { Button, Divider, Stack, Typography, CircularProgress, Grid, Tooltip } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import { useSelector } from 'react-redux'

import { PLGChip } from '../PLG'
import ContactSalesDialog from '../ContactSalesDialog'

import { useIntegrationsData } from './useIntegrationsData'

import { ReactComponent as ArrowCircleUp } from '@/assets/icons/arrow-circle-up.svg'
import { usePermissionsV2 } from '@/hooks/usePermissionsV2'

export default function IntegrationsTabContent() {
  const { freeIntegrations, limitedIntegrations } = useIntegrationsData()
  const { allocationSource } = usePermissionsV2()
  const [openContactSales, setOpenContactSales] = useState(false)
  const profile = useSelector(({ user: { profile } }) => profile)
  const isTeacher = profile?.isTeacher || false

  return (
    <>
      <ContactSalesDialog open={openContactSales} onClose={() => setOpenContactSales(false)} />
      <Stack gap="24px">
        <Stack direction="row" justifyContent="space-between">
          <Stack gap="4px">
            <Typography color="#101828" fontSize={18} fontWeight={600} lineHeight="28px">
              Integrations & connected apps
            </Typography>
            <Typography color="#475467" lineHeight="20px">
              Supercharge your workflow and connect the tool you use every day.
            </Typography>
          </Stack>
          <Button
            color="secondary"
            startIcon={<AddRoundedIcon />}
            sx={{ alignSelf: 'flex-start', height: 40, flexShrink: 0 }}
            variant="outlined"
            onClick={() => setOpenContactSales(true)}
          >
            Request integration
          </Button>
        </Stack>
        <Divider sx={{ borderColor: '#EAECF0' }} />
        <Stack gap="16px">
          <Typography color="#344054" fontSize={14} fontWeight={600} lineHeight="20px">
            Free Integrations
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={3}>
            {freeIntegrations
              .filter(integration => integration.visible)
              .map(integration => (
                <IntegrationCard key={integration.id} {...integration} />
              ))}
          </Stack>
        </Stack>
        {isTeacher ? (
          <>
            <Divider sx={{ borderColor: '#EAECF0' }} />
            <Stack gap="16px">
              <Typography color="#344054" fontSize={14} fontWeight={600} lineHeight="20px">
                LTI Integrations
              </Typography>
              {allocationSource !== 'organization' && <LTIUpgradePLG />}
              <Grid container spacing={2}>
                {limitedIntegrations
                  .filter(integration => integration.visible)
                  .map(integration => (
                    <Grid key={integration.id} item md={4} sm={6} xs={12}>
                      <IntegrationCard {...integration} />
                    </Grid>
                  ))}
              </Grid>
            </Stack>
          </>
        ) : null}
      </Stack>
    </>
  )
}

export function IntegrationCard({
  icon: Icon,
  title,
  description,
  connected = false,
  onConnect,
  onLearnMore,
  learnMoreLabel = 'Learn more',
  loading = false,
  disabled = false,
  tooltipContent = '',
}) {
  return (
    <Stack
      sx={{
        minWidth: 320,
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: '1 0 0',
        borderRadius: '12px',
        border: '1px solid #EAECF0',
        background: '#FFF',
        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      }}
    >
      <Stack gap={3} p={3} width="100%">
        <Stack direction="row" gap="8px" justifyContent="space-between" width="100%">
          <Stack alignItems="center" direction="row" gap="12px">
            <Icon />
            <Typography color="#101828" fontSize={16} fontWeight={600} lineHeight="24px">
              {title}
            </Typography>
          </Stack>
          {onConnect ? (
            <Tooltip arrow placement="top" title={disabled ? tooltipContent : ''}>
              <span>
                <Button
                  color="secondary"
                  disabled={loading || disabled}
                  startIcon={
                    loading ? (
                      <CircularProgress size={12} />
                    ) : (
                      <FiberManualRecordRoundedIcon
                        sx={{ height: 12, width: 12, color: connected ? '#17B26A' : '#F04438' }}
                      />
                    )
                  }
                  sx={{ alignSelf: 'flex-start' }}
                  variant="outlined"
                  onClick={onConnect}
                >
                  {loading ? 'Connecting...' : connected ? 'Connected' : 'Disconnected'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
        <Typography color="#475467" lineHeight="20px">
          {description}
        </Typography>
      </Stack>
      <Stack alignItems="flex-end" pb={1} pr={2} width="100%">
        <Button color="primary" sx={{ textTransform: 'none', color: '#A12F7E' }} variant="text" onClick={onLearnMore}>
          {learnMoreLabel}
        </Button>
      </Stack>
    </Stack>
  )
}

export function LTIUpgradePLG() {
  return (
    <Stack background="#FFF" border="1px solid #F2F4F7" borderRadius="10px" gap="24px" p="12px 16px" width="100%">
      <Stack gap="12px" width="100%">
        <Stack direction="row" gap="6px" justifyContent="space-between">
          <Stack alignItems="center" direction="row" gap="6px">
            <ArrowCircleUp />
            <Typography color="#101828" fontSize={16} fontWeight={600} lineHeight="24px">
              Upgrade to Organizaition Plan
            </Typography>
          </Stack>
          <PLGChip type="organization" />
        </Stack>
        <Divider sx={{ borderColor: '#EAECF0' }} />
      </Stack>
      <Stack alignItems="center" direction="row" gap="12px" justifyContent="space-between" width="100%">
        <Typography color="#475467" lineHeight="20px" width="90%">
          Add Organization-wide LTI integrations at your school or district.
        </Typography>
        <Button
          href="https://speakable.io/schools-districts"
          rel="noopener noreferrer"
          sx={{
            width: '150px',
          }}
          target="_blank"
          variant="contained"
        >
          Learn More
        </Button>
        {/* <Button fullWidth color="primary" variant="contained" onClick={() => null}>
          Get started
        </Button> */}
      </Stack>
    </Stack>
  )
}
